<template>
  <main class="integration-uis">
    <div class="integration-uis__container">
      <button-back />
      <h1 class="integration-uis__title">Настройка интеграции UIS</h1>
      <div class="integration-uis__connection">
        <p v-if="errorText">{{ errorText }}</p>
        <template v-if="needFillApiKey">
          <input-text
            class="settings-editing__link"
            placeholder="Введите ключ API"
            title="Ключ API"
            v-bind:violations="violationCauses['api_key']"
            v-model="apiKey"
          />
          <button
            class="integration-uis__button"
            type="button"
            v-on:click="onSubmitConnect"
            v-bind:disabled="isDisabledSubmitButton"
          >Сохранить</button>
        </template>
        <template v-else-if="canChangeApiKey">
          <button
            v-if="!showEditSection"
            class="integration-uis__key-edit"
            type="button"
            v-on:click="showEditSection = true"
          >
            Изменить ключ API
            <div class="integration-uis__key-edit__icon" />
          </button>
          <template v-else>
            <input-text
              class="settings-editing__link"
              placeholder="Введите ключ API"
              title="Ключ API"
              v-bind:violations="violationCauses['api_key']"
              v-model="apiKey"
            />
            <button
              class="integration-uis__button"
              type="button"
              v-on:click="onEditConnect"
              v-bind:disabled="isDisabledSubmitButton"
            >Изменить</button>
          </template>
        </template>
      </div>
      <section
        class="integration-uis__control"
        v-if="canLinkEmployees"
      >
        <matching-external-employees
          external-employees-column-head-title="Сотрудник UIS"
          v-bind:employees="employees"
          v-bind:external-employees="uisEmployeeList"
          v-on:link-employee="onLinkEmployee"
        />
      </section>
      <support-contacts
        class="integration-uis__contacts"
        title="Если у вас возникли вопросы, свяжитесь с нами:" />
    </div>
  </main>
</template>
<script>

import InputText from '@/components/InputText.vue';
import SupportContacts from '@/components/SupportContacts.vue';
import notifierMixin from '@/mixins/notifierMixin';
import MatchingExternalEmployees from '@/components/MatchingExternalEmployees.vue';
import ButtonBack from '@/components/ButtonBack.vue';

/**
 * @description Действие ввода ключа API.
 * @type {string}
 */
const ACTION_FILL_API_KEY = 'fill-api-key';

/**
 * @description Действие изменения ключа API.
 * @type {string}
 */
const ACTION_CHANGE_API_KEY = 'change-api-key';

/**
 * @description Действие изменения ключа API.
 * @type {string}
 */
const ACTION_SET_DISPLAYED_PHONE_NUMBER = 'set-displayed-phone-number';

/**
 * @description Действие связывания сотрудников МИС с UIS.
 * @type {string}
 */
const ACTION_LINK_EMPLOYEES = 'link-employees';

const EMPTY_ERROR = '';

/**
 * @description Страница интеграции с UIS.
 * @type {Vue}
 */
export default {
  components: {
    ButtonBack,
    InputText,
    SupportContacts,
    MatchingExternalEmployees,
  },
  computed: {
    /**
     * @description Может изменить ключ API.
     * @type {boolean}
     */
    canChangeApiKey() {
      return this.actions.includes(ACTION_CHANGE_API_KEY);
    },
    /**
     * @description Может изменить ключ API.
     * @type {boolean}
     */
    canSetDisplayedPhoneNumber() {
      return this.actions.includes(ACTION_SET_DISPLAYED_PHONE_NUMBER);
    },
    /**
     * @description Может связывать сотрудников.
     * @type {boolean}
     */
    canLinkEmployees() {
      return this.actions.includes(ACTION_LINK_EMPLOYEES);
    },
    /**
     * @description Сообщение об ошибке.
     * @type {string}
     */
    errorText() {
      const { error } = this.violationCauses;
      if (error) {
        return error.join('\n');
      }
      return EMPTY_ERROR;
    },
    /**
     * @description Деактивировать кнопку отправки.
     * @type {boolean}
     */
    isDisabledSubmitButton() {
      return this.isAwaitRequest;
    },
    /**
     * @description Необходимо ввести ключ API.
     * @type {boolean}
     */
    needFillApiKey() {
      return this.actions.includes(ACTION_FILL_API_KEY);
    },
    /**
     * @description Идентификаторы выбранных сотрудников UIS.
     * @type {number[]}
     */
    selectedExternalEmployeeIds() {
      return this
        .employees
        .reduce((ids, candidate) => {
          const id = candidate.getExternalId();
          return [...ids, ...(id ? [id] : [])];
        }, []);
    },
    /**
     * @description Список сотрудников UIS.
     * @type {any}
     */
    uisEmployeeList() {
      return this
        .uisEmployees
        .map((item) => ({
          ...item,
          disabled: this.selectedExternalEmployeeIds.includes(item.id),
        }));
    },
    phones() {
      return this.phoneList.map((phone) => ({ phone }));
    },
  },
  created() {
    this.fetchIntegration();
  },
  data() {
    return {
      /**
       * @description Действия.
       * @type {string[]}
       */
      actions: [],
      /**
       * @description Ключ доступа к API.
       * @type {string}
       */
      apiKey: '',
      /**
       * @description Сотрудники с доступом к телефонии.
       * @type {Employee[]}
       */
      employees: [],
      /**
       * @description Сотрудники из UIS.
       * @type {UisEmployee[]}
       */
      uisEmployees: [],
      /**
       * @description Ожидание завершения запроса.
       * @type {boolean}
       */
      isAwaitRequest: false,
      /**
       * @description Показывать секцию изменения ключа API.
       * @type {boolean}
       */
      showEditSection: false,
      /**
       * @type Array
       */
      violationCauses: [],
      phoneList: [],
      phone: null,
    };
  },
  methods: {
    /**
     * @description Получить интеграцию.
     */
    fetchIntegration() {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      const onSuccess = (integration) => {
        this.actions = integration.actions;
        this.employees = integration.employees;
        this.uisEmployees = integration.externalEmployees;
        this.phone = integration.phone;
        this.phoneList = integration.phoneList;
      };
      this
        .$telephonyIntegrationGateway
        .viewUis()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать редактирование интеграции.
     * @returns {void}
     */
    onEditConnect() {
      const onFulfilled = (result) => {
        result.forEach(({ title }) => {
          this.notifySuccess(title);
          this.fetchIntegration();
          this.showEditSection = false;
          this.violationCauses = [];
        });
        result.forEachLeft((error) => {
          this.notifyFail(error.getTitle());
          this.violationCauses = error.allCauses();
        });
      };
      const params = { api_key: this.apiKey };
      this.isAwaitRequest = true;
      this
        .$telephonyIntegrationGateway
        .editUis(params)
        .then(onFulfilled)
        .finally(() => { this.isAwaitRequest = false; });
    },
    /**
     * @description Обработать связывание сотрудника МИС c UIS.
     * @param {Employee} employee   Сотрудник.
     * @param {?number}  externalId Идентификатор сотрудника UIS.
     * @returns {void}
     */
    onLinkEmployee(employee, externalId) {
      const onFail = (error) => this.notifyFail(error.getTitle());
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.employees = this.employees.map(
          (candidate) => (candidate === employee ? employee.setExternalId(externalId) : candidate),
        );
      };
      this
        .$telephonyIntegrationGateway
        .linkEmployeeUis(employee.getId(), externalId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать подключение к интеграции.
     * @returns {void}
     */
    onSubmitConnect() {
      const onFulfilled = (result) => {
        result.forEach(({ title }) => {
          this.notifySuccess(title);
          this.fetchIntegration();
          this.violationCauses = [];
        });
        result.forEachLeft((error) => {
          this.notifyFail(error.getTitle());
          this.violationCauses = error.allCauses();
        });
      };
      const params = { api_key: this.apiKey };
      this.isAwaitRequest = true;
      this
        .$telephonyIntegrationGateway
        .createUis(params)
        .then(onFulfilled)
        .finally(() => { this.isAwaitRequest = false; });
    },
    /**
     * @description Обработать ввод телефона.
     * @returns {void}
     */
    onInputPhone(phone) {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.violationCauses = error.allCauses();
      };
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.fetchIntegration();
        this.violationCauses = [];
      };
      this.isAwaitRequest = true;
      this
        .$telephonyIntegrationGateway
        .setUisOutgoingPhoneNumber(phone)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
  },
  mixins: [
    notifierMixin,
  ],
  name: 'ClinicPanelIntegrationUis',
};
</script>
<style lang="scss">
@use "@/scss/buttons";

.integration-uis {
  width: 100%;
  height: 100%;
  overflow: scroll;
  &__container {
    padding: 38px 20px 48px 23px;
    width: 100%;
  }
  &__title {
    font-weight: 600;
    font-size: 38px;
    line-height: 46px;
    color: #000000;
    margin: 0 0 22px 0;
  }
  &__subtitle {
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    a:hover {
      text-decoration: none;
    }
  }
  &__connection {
    background-color: var(--section-background-color);
    padding: 40px 50px 35px 40px;
    margin: 46px 0 0 0;
  }
  &__button {
    @include buttons.btn-blue(244px, 48px, 16px);
    max-width: 100%;
    margin: 40px 0 0 0;
    &:disabled {
      background-color: var(--unfocus-color);
    }
    &-edit {
      background-image: url("@/assets/img/pen-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 15px;
      height: 15px;
      padding: 0;
      margin-left: 14px;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/pen-blue.svg");
      }
    }
  }
  &__control {
    border-top: 1px solid #C2CFE0;
    background-color: var(--section-background-color);
    padding: 30px 50px 60px 40px;
  }
  &__key-edit {
    display: flex;
    cursor: pointer;
    &:hover {
      .integration__key-edit__icon {
        background-image: url("@/assets/img/pen-blue.svg");
      }
    }
    &__icon {
      background-image: url("@/assets/img/pen-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 15px;
      height: 15px;
      padding: 0;
      margin-left: 14px;

    }
  }
  &__contacts {
    margin: 46px 0 0 0;
  }
}
@media screen and (max-width: 1240px) {
  .integration-uis  {
    &__container {
      padding: 38px 20px 145px;
    }
  }
}
@media screen and (max-width: 640px) {
  .integration-uis  {
    &__container {
      padding: 38px 10px 140px;
    }
    &__connection {
      padding: 40px 10px 35px;
    }
    &__control {
      padding: 30px 10px 60px;
    }
  }
}
@media screen and (max-width: 480px) {
  .integration {
    &__connection {
      background-color: transparent;
      padding: 0 0 20px 0;
    }
    &__control {
      padding: 20px 0 0 0;
      background-color: transparent;
    }
  }
}
</style>
